import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RotateCcw, PackageX, Check, X, Clock, Wallet, ImagePlus, Loader2 } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";

const REASONS = ["Item damaged or defective", "Wrong item received", "Item not as described", "Arrived too late", "No longer needed", "Other"];
const statusCls = { requested: "bg-amber-500", approved: "bg-blue-500", refunded: "bg-green-600", rejected: "bg-red-500" };
const statusLabel = { requested: "Pending review", approved: "Approved", refunded: "Refunded", rejected: "Declined" };

const ReturnTimeline = ({ status }) => {
  const rejected = status === "rejected";
  const done = status === "refunded" || rejected;
  const steps = [
    { key: "submitted", label: "Submitted", state: "done" },
    { key: "review", label: "Under review", state: done ? "done" : "active" },
    {
      key: "result",
      label: rejected ? "Declined" : "Refunded to wallet",
      state: status === "refunded" ? "done" : rejected ? "rejected" : "pending",
    },
  ];
  return (
    <div className="flex items-center mt-3" data-testid="return-timeline">
      {steps.map((s, i) => {
        const isRej = s.state === "rejected";
        const isDone = s.state === "done";
        const isActive = s.state === "active";
        return (
          <div key={s.key} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-[11px] shrink-0 ${isRej ? "bg-red-500" : isDone ? "bg-green-600" : isActive ? "bg-amber-500 animate-pulse" : "bg-slate-300 dark:bg-slate-600"}`}>
                {isRej ? <X className="w-3.5 h-3.5" /> : isDone ? <Check className="w-3.5 h-3.5" /> : isActive ? <Clock className="w-3 h-3" /> : i + 1}
              </span>
              <span className={`text-[10px] leading-tight text-center whitespace-nowrap ${isDone || isRej ? "font-semibold" : "text-muted-foreground"}`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && <div className={`h-0.5 flex-1 mx-1 mb-4 rounded ${steps[i + 1].state === "done" || steps[i + 1].state === "rejected" ? (steps[i + 1].state === "rejected" ? "bg-red-400" : "bg-green-500") : "bg-slate-200 dark:bg-slate-700"}`} />}
          </div>
        );
      })}
    </div>
  );
};

export const BuyerReturns = ({ prefillOrder, onConsumePrefill, orders = [] }) => {
  const { format } = useCurrency();
  const [returns, setReturns] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ order_id: "", type: "return", reason: "", comment: "", photos: [] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const mediaUrl = (u) => (u && u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

  const load = useCallback(() => { api.get("/buyer/returns").then((r) => setReturns(r.data)).catch(() => setReturns([])); }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (prefillOrder) {
      setForm({ order_id: prefillOrder.order_id, type: "return", reason: "", comment: "", photos: [] });
      setOpen(true);
      onConsumePrefill?.();
    }
  }, [prefillOrder, onConsumePrefill]);

  const eligible = orders.filter((o) => ["delivered", "shipped"].includes((o.fulfillment_status || o.status || "").toLowerCase()));

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (form.photos.length + files.length > 6) return toast.error("Up to 6 photos");
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/buyer/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      }
      setForm((f) => ({ ...f, photos: [...f.photos, ...uploaded] }));
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const removePhoto = (u) => setForm((f) => ({ ...f, photos: f.photos.filter((p) => p !== u) }));

  const submit = async () => {
    if (!form.order_id) return toast.error("Select an order");
    if (!form.reason) return toast.error("Select a reason");
    setSaving(true);
    try {
      await api.post("/buyer/returns", form);
      toast.success("Request submitted — we'll review it shortly");
      setOpen(false); setForm({ order_id: "", type: "return", reason: "", comment: "", photos: [] }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Manage returns and refund requests for your delivered or shipped orders.</p>
        <Button className="rounded-full" data-testid="new-return-btn" onClick={() => { setForm({ order_id: "", type: "return", reason: "", comment: "", photos: [] }); setOpen(true); }}>
          <RotateCcw className="w-4 h-4 mr-2" />New request
        </Button>
      </div>

      {returns === null ? (
        <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-20 rounded-2xl shimmer" />)}</div>
      ) : returns.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground rounded-2xl">
          <PackageX className="w-10 h-10 mx-auto mb-3 opacity-40" />No return or refund requests yet.
        </Card>
      ) : (
        <div className="space-y-3">
          {returns.map((r) => (
            <Card key={r.return_id} data-testid={`return-row-${r.return_id}`} className="p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-mono text-xs text-muted-foreground">Order #{r.order_ref}</p>
                  <p className="font-medium text-sm mt-0.5 capitalize">{r.type} · {r.reason}</p>
                  {r.comment && <p className="text-xs text-muted-foreground mt-1">{r.comment}</p>}
                  <p className="text-[11px] text-muted-foreground mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
                <div className="text-right shrink-0">
                  <Badge className={`${statusCls[r.status] || "bg-slate-400"}`}>{statusLabel[r.status] || r.status}</Badge>
                  <p className="text-sm font-semibold mt-1">{format(r.amount || 0)}</p>
                </div>
              </div>
              <ReturnTimeline status={r.status} />
              {r.photos?.length > 0 && (
                <div className="flex gap-2 mt-3" data-testid={`return-photos-${r.return_id}`}>
                  {r.photos.map((p, i) => (
                    <a key={i} href={mediaUrl(p)} target="_blank" rel="noreferrer" className="w-14 h-14 rounded-lg overflow-hidden border border-border/60 shrink-0">
                      <img src={mediaUrl(p)} alt="return evidence" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
              {r.status === "refunded" && (
                <div className="mt-3 flex items-center gap-2 text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/30 rounded-lg px-3 py-2" data-testid={`return-credited-${r.return_id}`}>
                  <Wallet className="w-4 h-4 shrink-0" />
                  <span><strong>{format(r.refunded_amount || r.amount || 0)}</strong> added to your Store Credit wallet.</span>
                </div>
              )}
              {r.status === "rejected" && r.admin_note && (
                <div className="mt-3 flex items-start gap-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 rounded-lg px-3 py-2">
                  <X className="w-4 h-4 shrink-0 mt-0.5" /><span>{r.admin_note}</span>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle>Request a return / refund</DialogTitle>
            <DialogDescription>Tell us what went wrong and we'll get it sorted.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Order</Label>
              <Select value={form.order_id} onValueChange={(v) => setForm({ ...form, order_id: v })}>
                <SelectTrigger data-testid="return-order-select" className="mt-1"><SelectValue placeholder="Select an order" /></SelectTrigger>
                <SelectContent>
                  {eligible.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No eligible orders (must be shipped/delivered)</div>}
                  {eligible.map((o) => <SelectItem key={o.order_id} value={o.order_id}>#{o.order_id.slice(-8)} · {format(o.total || 0)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="return-type-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="return">Return item</SelectItem><SelectItem value="refund">Refund only</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger data-testid="return-reason-select" className="mt-1"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Comment (optional)</Label>
              <Textarea data-testid="return-comment" className="mt-1" rows={3} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Add any details…" />
            </div>
            <div>
              <Label className="text-xs">Photos (optional) — show us the issue</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {form.photos.map((p) => (
                  <div key={p} className="relative w-16 h-16 rounded-lg overflow-hidden border border-border/60 group">
                    <img src={mediaUrl(p)} alt="upload" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePhoto(p)} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid="remove-photo-btn"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                {form.photos.length < 6 && (
                  <label className="w-16 h-16 rounded-lg border-2 border-dashed border-border/70 flex items-center justify-center cursor-pointer hover:border-primary/60 hover:bg-muted/30 transition-colors" data-testid="return-photo-upload">
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /> : <ImagePlus className="w-5 h-5 text-muted-foreground" />}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={uploadPhoto} disabled={uploading} />
                  </label>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full w-full" data-testid="submit-return-btn" onClick={submit} disabled={saving}>{saving ? "Submitting…" : "Submit request"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
